import React, { useState, useEffect } from "react";
import instance from "../../instance";

import "../Customer.css";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const SpeechSubscriptions = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const code = params.get("code");

  const [speechData, setSpeechData] = useState(null);
  const [isEditingSpeech, setIsEditingSpeech] = useState(false);
  const [paymentDate, setPaymentDate] = useState(null);
  const [speechFormData, setSpeechFormData] = useState({
    StartDate: null,
    EndDate: null,
    IsActive: false,
    Price: "1000",
  });

  useEffect(() => {
    if (code) {
      fetchSpeechData();
      fetchPaymentDate();
    } else {
      toast.error("No code provided.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchers are recreated each render; adding them would refetch on every paint
  }, [code]);

  const fetchPaymentDate = async () => {
    try {
      const response = await instance.get(`payment/by-code/${code}`);
      if (response.status === 200 && response.data?.data?.PaymentDate) {
        const newPaymentDate = new Date(response.data.data.PaymentDate);
        setPaymentDate(newPaymentDate);
        
        // If IsActive is true and we're editing, update EndDate to PaymentDate
        if (speechFormData.IsActive && isEditingSpeech) {
          setSpeechFormData((prevData) => ({
            ...prevData,
            EndDate: newPaymentDate,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching payment date:", error);
    }
  };

  const fetchSpeechData = async () => {
    try {
      const response = await instance.get(`speech-subscriptions/by-code/${code}`);

      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = response.data;
      if (data && data.data) {
        setSpeechData(data.data);
        setSpeechFormData({
          StartDate: data.data.StartDate ? new Date(data.data.StartDate) : null,
          EndDate: data.data.EndDate ? new Date(data.data.EndDate) : null,
          IsActive: data.data.IsActive || false,
          Price: data.data.Price || "",
        });
      } else {
        toast.error("No speech subscription data found for the given code.");
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // No subscription found - this is okay, user can create one
        setSpeechData(null);
      } else {
        console.error("Error fetching speech subscription data:", error);
        toast.error("An error occurred while fetching speech subscription data.");
      }
    }
  };

  const toggleEditSpeech = async () => {
    if (isEditingSpeech) {
      saveSpeechData();
    } else {
      // Initialize form with default values when adding new subscription
      if (!speechData) {
        // Fetch payment date before initializing
        await fetchPaymentDate();
        setSpeechFormData({
          StartDate: null,
          EndDate: null,
          IsActive: false,
          Price: "0",
        });
      }
      setIsEditingSpeech(true);
    }
  };

  const handleSpeechInputChange = (event) => {
    const { name, value } = event.target;
    setSpeechFormData({ ...speechFormData, [name]: value === "true" || value === "false" ? value === "true" : value });
  };

  const handleStartDateChange = (date) => {
    setSpeechFormData({ ...speechFormData, StartDate: date });
  };

  const handleEndDateChange = (date) => {
    setSpeechFormData({ ...speechFormData, EndDate: date });
  };

  const handleIsActiveChange = (e) => {
    const isActive = e.target.value === "true";
    setSpeechFormData((prevData) => {
      return {
        ...prevData,
        IsActive: isActive,
        StartDate: isActive ? (prevData.StartDate || new Date()) : null,
        EndDate: isActive && paymentDate ? new Date(paymentDate) : null,
        Price: isActive ? "1000" : "0",
      };
    });
  };

  const calculateRemainingDays = () => {
    // استخدام speechFormData عند التعديل، و speechData عند العرض
    const data = isEditingSpeech ? speechFormData : speechData;
    if (!data || !data.EndDate || !data.IsActive) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endDate = data.EndDate instanceof Date ? new Date(data.EndDate) : new Date(data.EndDate);
    endDate.setHours(0, 0, 0, 0);

    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const calculateValue = () => {
    // استخدام speechFormData عند التعديل، و speechData عند العرض
    const data = isEditingSpeech ? speechFormData : speechData;
    if (!data || !data.StartDate || !data.IsActive || !data.Price) {
      return null;
    }

    const startDate = data.StartDate instanceof Date ? new Date(data.StartDate) : new Date(data.StartDate);
    startDate.setHours(0, 0, 0, 0);

    // تحديد تاريخ النهاية (EndDate إذا كان موجود، وإلا اليوم)
    const endDate = data.EndDate ? (data.EndDate instanceof Date ? new Date(data.EndDate) : new Date(data.EndDate)) : new Date();
    endDate.setHours(0, 0, 0, 0);

    // إذا كان تاريخ النهاية في الماضي، لا تحسب القيمة
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (data.EndDate && endDate < today) {
      return null;
    }

    const price = parseFloat(data.Price) || 0;
    let totalValue = 0;
    const monthDetails = [];

    // التحقق إذا كان StartDate و EndDate في نفس اليوم من الشهر (مثلاً 22/06 و 22/07)
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const isSameDayOfMonth = startDay === endDay;

    // إذا كانا في نفس الشهر
    if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
      const month = startDate.getMonth();
      const year = startDate.getFullYear();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const daysCount = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      const dailyRate = price / daysInMonth;
      const monthValue = dailyRate * daysCount;
      totalValue = monthValue;
      
      monthDetails.push({
        month: month + 1,
        year: year,
        daysInMonth: daysInMonth,
        daysCount: daysCount,
        dailyRate: dailyRate,
        value: monthValue,
      });
    } else if (isSameDayOfMonth) {
      // إذا كانا في نفس اليوم من الشهر (مثلاً 22/06 و 22/07)، احسب شهر كامل = price لكل شهر
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const month = currentDate.getMonth();
        const year = currentDate.getFullYear();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        // حساب شهر كامل = price
        const dailyRate = price / daysInMonth;
        const monthValue = price; // شهر كامل = price
        
        totalValue += monthValue;
        
        monthDetails.push({
          month: month + 1,
          year: year,
          daysInMonth: daysInMonth,
          daysCount: daysInMonth,
          dailyRate: dailyRate,
          value: monthValue,
        });
        
        // الانتقال إلى نفس اليوم من الشهر التالي
        const nextMonth = month + 1;
        const nextYear = nextMonth > 11 ? year + 1 : year;
        const nextMonthAdjusted = nextMonth > 11 ? 0 : nextMonth;
        const daysInNextMonth = new Date(nextYear, nextMonthAdjusted + 1, 0).getDate();
        const dayToSet = Math.min(startDay, daysInNextMonth);
        currentDate = new Date(nextYear, nextMonthAdjusted, dayToSet);
        
        // إذا تجاوزنا EndDate، توقف
        if (currentDate > endDate) break;
      }
    } else {
      // إذا كانا في شهرين مختلفين
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const month = currentDate.getMonth();
        const year = currentDate.getFullYear();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        // تحديد أول يوم وآخر يوم في هذا الشهر
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        
        // تحديد تاريخ البداية الفعلي لهذا الشهر
        const monthStart = currentDate > firstDayOfMonth ? currentDate : firstDayOfMonth;
        
        // تحديد تاريخ النهاية الفعلي لهذا الشهر
        const monthEnd = endDate < lastDayOfMonth ? endDate : lastDayOfMonth;
        
        // حساب عدد الأيام في هذا الشهر
        const daysCount = Math.ceil((monthEnd - monthStart) / (1000 * 60 * 60 * 24)) + 1;
        
        // حساب القيمة لهذا الشهر
        const dailyRate = price / daysInMonth;
        const monthValue = dailyRate * daysCount;
        totalValue += monthValue;
        
        monthDetails.push({
          month: month + 1,
          year: year,
          daysInMonth: daysInMonth,
          daysCount: daysCount,
          dailyRate: dailyRate,
          value: monthValue,
        });
        
        // الانتقال إلى أول يوم من الشهر التالي
        currentDate = new Date(year, month + 1, 1);
      }
    }

    return {
      value: totalValue,
      monthDetails: monthDetails,
    };
  };

  const saveSpeechData = async () => {
    const formattedData = {
      StartDate: speechFormData.StartDate
        ? speechFormData.StartDate.toISOString().split("T")[0]
        : null,
      EndDate: speechFormData.EndDate
        ? speechFormData.EndDate.toISOString().split("T")[0]
        : null,
      IsActive: speechFormData.IsActive,
      Price: speechFormData.Price || "0",
    };

    try {
      if (speechData) {
        // Update existing subscription
        const response = await instance.put(
          `speech-subscriptions/by-code/${code}`,
          formattedData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status !== 200) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        toast.success("Speech subscription updated successfully!");
        fetchSpeechData(); // Fetch updated data
        setIsEditingSpeech(false);
      } else {
        // Create new subscription using code
        try {
          const createResponse = await instance.post(
            `speech-subscriptions/by-code/${code}`,
            formattedData,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (createResponse.status !== 201) {
            throw new Error(`HTTP error! status: ${createResponse.status}`);
          }

          toast.success("Speech subscription created successfully!");
          fetchSpeechData();
          setIsEditingSpeech(false);
        } catch (error) {
          console.error("Error creating speech subscription:", error);
          toast.error("An error occurred while creating speech subscription.");
        }
      }
    } catch (error) {
      console.error("Error saving speech subscription:", error);
      toast.error("An error occurred while saving speech subscription.");
    }
  };

  return (
    <div>
      <h2>Speech</h2>
          {speechData ? (
            <ul>
              <li>
                Start Date:{" "}
                <span
                  className={`info ${isEditingSpeech ? "hidden" : ""}`}
                  id="info-StartDate"
                >
                  {speechData.StartDate &&
                    new Date(speechData.StartDate).toLocaleDateString("en-GB")}
                </span>
                {isEditingSpeech && (
                  <DatePicker
                    className="input-field"
                    selected={speechFormData.StartDate}
                    onChange={handleStartDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Start Date"
                  />
                )}
              </li>
              <li>
                End Date:{" "}
                <span
                  className={`info ${isEditingSpeech ? "hidden" : ""}`}
                  id="info-EndDate"
                >
                  {speechData.EndDate &&
                    new Date(speechData.EndDate).toLocaleDateString("en-GB")}
                </span>
                {isEditingSpeech && (
                  <DatePicker
                    className="input-field"
                    selected={speechFormData.EndDate}
                    onChange={handleEndDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select End Date"
                  />
                )}
              </li>
              <li>
                Is Active:{" "}
                <span
                  className={`info ${isEditingSpeech ? "hidden" : ""}`}
                  id="info-IsActive"
                  style={{
                    background: speechData.IsActive
                      ? "rgba(0, 255, 0, 0.3)"
                      : "rgba(255, 0, 0, 0.3)",
                  }}
                >
                  {speechData.IsActive ? "True" : "False"}
                </span>
                {isEditingSpeech && (
                  <select
                    className="input-field"
                    name="IsActive"
                    value={speechFormData.IsActive ? "true" : "false"}
                    onChange={handleIsActiveChange}
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                )}
              </li>
              <li>
                Price:{" "}
                <span
                  className={`info ${isEditingSpeech ? "hidden" : ""}`}
                  id="info-Price"
                >
                  {speechData.Price}
                </span>
                {isEditingSpeech && (
                  <input
                    className="input-field"
                    type="number"
                    name="Price"
                    value={speechFormData.Price}
                    onChange={handleSpeechInputChange}
                    placeholder="Enter Price"
                  />
                )}
              </li>
              <li>
              Statistics:{" "}
                <span
                  className="info"
                  id="info-Value"
                  style={{
                    background: "rgba(0, 150, 255, 0.3)",
                    fontWeight: "bold",
                  }}
                >
                  {(() => {
                    const valueData = calculateValue();
                    if (valueData === null) return "N/A";
                    
                    if (valueData.monthDetails.length === 1) {
                      const detail = valueData.monthDetails[0];
                      return `${valueData.value.toFixed(2)} (${detail.dailyRate.toFixed(2)} × ${detail.daysCount} يوم من ${detail.daysInMonth} يوم)`;
                    } else {
                      const detailsText = valueData.monthDetails.map((detail, index) => 
                        `الشهر ${index + 1}: ${detail.dailyRate.toFixed(2)} × ${detail.daysCount} يوم من ${detail.daysInMonth} يوم = ${detail.value.toFixed(2)}`
                      ).join(' | ');
                      return `${valueData.value.toFixed(2)} (${detailsText})`;
                    }
                  })()}
                </span>
              </li>
              <li>
                Remaining:{" "}
                <span
                  className="info"
                  id="info-Remaining"
                  style={{
                    background: (() => {
                      const remaining = calculateRemainingDays();
                      if (remaining === null) return "rgba(128, 128, 128, 0.3)";
                      if (remaining < 0) return "rgba(255, 0, 0, 0.3)";
                      if (remaining <= 7) return "rgba(255, 165, 0, 0.3)";
                      return "rgba(0, 255, 0, 0.3)";
                    })(),
                  }}
                >
                  {(() => {
                    const remaining = calculateRemainingDays();
                    if (remaining === null) return "N/A";
                    if (remaining < 0) return `Expired (${Math.abs(remaining)} days ago)`;
                    return `${remaining} days`;
                  })()}
                </span>
              </li>
            </ul>
          ) : (
            <div>
              {isEditingSpeech ? (
                <ul>
                  <li>
                    Start Date:{" "}
                    <DatePicker
                      className="input-field"
                      selected={speechFormData.StartDate}
                      onChange={handleStartDateChange}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select Start Date"
                    />
                  </li>
                  <li>
                    End Date:{" "}
                    <DatePicker
                      className="input-field"
                      selected={speechFormData.EndDate}
                      onChange={handleEndDateChange}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select End Date"
                    />
                  </li>
                  <li>
                    Is Active:{" "}
                    <select
                      className="input-field"
                      name="IsActive"
                      value={speechFormData.IsActive ? "true" : "false"}
                      onChange={handleIsActiveChange}
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                  </li>
                  <li>
                    Price:{" "}
                    <input
                      className="input-field"
                      type="number"
                      name="Price"
                      value={speechFormData.Price}
                      onChange={handleSpeechInputChange}
                      placeholder="Enter Price"
                    />
                  </li>
                  <li>
                  Statistics:{" "}
                    <span
                      className="info"
                      style={{
                        background: "rgba(0, 150, 255, 0.3)",
                        fontWeight: "bold",
                      }}
                    >
                      {(() => {
                        const valueData = calculateValue();
                        if (valueData === null) return "N/A";
                        
                        if (valueData.monthDetails.length === 1) {
                          const detail = valueData.monthDetails[0];
                          return `${valueData.value.toFixed(2)} (${detail.dailyRate.toFixed(2)} × ${detail.daysCount} يوم من ${detail.daysInMonth} يوم)`;
                        } else {
                          const detailsText = valueData.monthDetails.map((detail, index) => 
                            `الشهر ${index + 1}: ${detail.dailyRate.toFixed(2)} × ${detail.daysCount} يوم من ${detail.daysInMonth} يوم = ${detail.value.toFixed(2)}`
                          ).join(' | ');
                          return `${valueData.value.toFixed(2)} (${detailsText})`;
                        }
                      })()}
                    </span>
                  </li>
                  <li>
                    Remaining:{" "}
                    <span
                      className="info"
                      style={{
                        background: (() => {
                          const remaining = calculateRemainingDays();
                          if (remaining === null) return "rgba(128, 128, 128, 0.3)";
                          if (remaining < 0) return "rgba(255, 0, 0, 0.3)";
                          if (remaining <= 7) return "rgba(255, 165, 0, 0.3)";
                          return "rgba(0, 255, 0, 0.3)";
                        })(),
                      }}
                    >
                      {(() => {
                        const remaining = calculateRemainingDays();
                        if (remaining === null) return "N/A";
                        if (remaining < 0) return `Expired (${Math.abs(remaining)} days ago)`;
                        return `${remaining} days`;
                      })()}
                    </span>
                  </li>
                </ul>
              ) : (
                <p style={{ color: "#808080" }}>No speech subscription found. Click "Add" to create one.</p>
              )}
            </div>
          )}
      <button className="edit-button" onClick={toggleEditSpeech}>
        {isEditingSpeech
          ? "Save"
          : speechData
          ? "Update"
          : "Add"}
      </button>
    </div>
  );
};

export default SpeechSubscriptions;

